import React, { useEffect, useState } from "react"
import { Link, matchRoutes, useLocation } from "react-router-dom"
import { Tag, Space, Tooltip, Popover } from "antd"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { getSidenavBadgeColor } from "utils/utils"

export type ActionType = {
  title: string
  icon: JSX.Element
  route?: string
  patterns?: { path: string }[]
  enabled: boolean
  badge: "New" | "Legacy" | "Coming Soon" | false
  newTab?: boolean
  id?: string
  subActions?: {
    id?: string
    title: string
    route: string
    patterns: { path: string }[]
    enabled: boolean
    badge?: "New" | "Legacy" | "Coming Soon" | false
  }[]
}

// Text that disappears when the rail is compact. It fades out ahead of the
// width animation (so nothing is seen being clipped) and fades back in once
// the panel has mostly opened.
export const fadeOutWhenCompact = (compact: boolean) =>
  `whitespace-nowrap transition-opacity ease-in-out motion-reduce:transition-none ${
    compact ? "opacity-0 duration-150" : "opacity-100 duration-200 delay-100"
  }`

export const fadeInWhenCompact = (compact: boolean) =>
  `transition-opacity ease-in-out motion-reduce:transition-none ${
    compact ? "opacity-100 duration-200 delay-100" : "opacity-0 duration-150"
  }`

export const SectionHeading: React.FC<{ title: string; compact: boolean }> = ({
  title,
  compact,
}) => (
  <div className="relative h-[18px]">
    <h6
      aria-hidden={compact}
      className={`absolute inset-0 pl-6 text-[12px] leading-[18px] text-a11y/60 ${fadeOutWhenCompact(
        compact
      )}`}
    >
      {title}
    </h6>
    {/* Compact rail: a hairline stands in for the heading. */}
    <span
      aria-hidden
      className={`absolute left-2 right-2 top-1/2 h-px bg-a11y/20 ${fadeInWhenCompact(
        compact
      )}`}
    />
  </div>
)

export const NavTab: React.FC<{
  id?: string
  action: ActionType
  index?: number
  compact?: boolean
  toggleSidebar?: VoidFunction
}> = ({ id, action, index, compact = false, toggleSidebar }) => {
  const { pathname } = useLocation()
  // const [allPatterns, setAllPatterns] = useState<{ path: string }[]>([])
  const [subActionsOpen, toggleSubActionsOpen] = useState<boolean>(false)

  const hasSubActions = action.subActions?.length! > 0
  const subPatterns =
    action.subActions
      ?.map((action) => action.patterns)
      .reduce((a, e) => [...a, ...e], []) ?? []
  const groupActive =
    hasSubActions && matchRoutes(subPatterns, pathname) !== null

  useEffect(() => {
    const allPatterns = action.subActions
      ?.map((action) => action.patterns)
      .reduce((a, e) => [...a, ...e], [])
    // setAllPatterns(allPatterns!)
    toggleSubActionsOpen(
      action.subActions?.length! > 0 &&
        matchRoutes(allPatterns!, pathname) !== null
    )
  }, [action.subActions, pathname])

  // Icons sit a step back from their label (design draws them at 60%), and come
  // up to full strength with the label on hover or when the row is active.
  const iconTint = (active: boolean) =>
    `shrink-0 flex items-center transition-colors duration-150 motion-reduce:transition-none ${
      active ? "text-a11y" : "text-a11y/60 group-hover:text-a11y"
    }`

  // Items keep `w-full` so the active pill shrinks in lock-step with the rail.
  // Horizontal padding widens in compact mode so the 20px icon sits centred in
  // the 40px rail.
  const itemPadding = `py-2 text-sm overflow-hidden transition-[padding] duration-300 ease-in-out motion-reduce:transition-none ${
    compact ? "px-[10px]" : "px-3"
  }`

  const badge = action.badge ? (
    <Space
      size={[0, 2]}
      wrap
    >
      <Tag
        color={getSidenavBadgeColor(action.badge)}
        className="px-1"
      >
        <span className="text-[10px]">{action.badge}</span>
      </Tag>
    </Space>
  ) : (
    ""
  )

  // Compact rail: the sub-links live in a flyout beside the icon instead of
  // the inline accordion.
  const flyout = (
    <div className="min-w-[180px] py-1 font-circular">
      <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-padeSubtext">
        {action.title}
      </p>
      {action?.subActions?.map((sub, index) =>
        !sub.enabled ? (
          ""
        ) : (
          <Link
            id={sub.id}
            key={index}
            to={sub.route}
            className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm hover:bg-primary50 hover:text-primary500 ${
              matchRoutes(sub.patterns!, pathname) !== null
                ? "bg-primary50 text-primary500 font-medium"
                : "text-padeBlack"
            }`}
            onClick={() => {
              toggleSidebar && toggleSidebar()
            }}
          >
            {sub.title}
            {sub.badge && (
              <Tag
                color={getSidenavBadgeColor(sub.badge)}
                className="mr-0"
              >
                <span className="text-xs font-circular ">{sub.badge}</span>
              </Tag>
            )}
          </Link>
        )
      )}
    </div>
  )

  if (hasSubActions) {
    const groupButton = (
      <button
        id={id}
        aria-label={compact ? action.title : undefined}
        className={`group w-full flex justify-between items-center gap-3 ${itemPadding} hover:bg-primary600 hover:text-a11y hover:rounded-md  ${
          (compact ? groupActive : subActionsOpen === true)
            ? `bg-primary600 text-a11y rounded-md font-circular tracking-wider`
            : "text-a11y/80"
        }`}
        onClick={() => {
          if (compact) return
          toggleSubActionsOpen(!subActionsOpen)
        }}
      >
        <div className="flex items-center justify-center gap-3 font-circular">
          <span className={iconTint(compact ? groupActive : subActionsOpen)}>
            {action.icon}
          </span>
          <span
            aria-hidden={compact}
            className={`flex items-center gap-3 ${fadeOutWhenCompact(compact)}`}
          >
            <span className="mt-0.5">{action.title}</span>
            {badge}
          </span>
        </div>
        <span
          className={`shrink-0 flex items-center ${fadeOutWhenCompact(
            compact
          )}`}
        >
          <ChevronDownIcon
            className={`w-4 transition-transform duration-300 ease-in-out ${
              subActionsOpen ? "rotate-180" : "rotate-0"
            }`}
          />
        </span>
      </button>
    )

    return (
      <div>
        {compact ? (
          <Popover
            placement="rightTop"
            trigger={["hover", "click"]}
            arrow={false}
            mouseEnterDelay={0.1}
            content={flyout}
            overlayInnerStyle={{ padding: 4 }}
            overlayStyle={{ paddingLeft: 8 }}
          >
            {groupButton}
          </Popover>
        ) : (
          groupButton
        )}

        <div
          aria-hidden={!subActionsOpen || compact}
          className={`sidebar-submenu ${
            subActionsOpen && !compact ? "sidebar-submenu--open" : ""
          }`}
        >
          <div className="sidebar-submenu__inner border-l border-a11y flex flex-col gap-0.5 ml-[30px] my-1 pl-2">
            {action?.subActions?.map((action, index) =>
              !action.enabled ? (
                ""
              ) : (
                <Link
                  id={action.id}
                  key={index}
                  to={action.route}
                  className={`w-full py-2 px-3 rounded-md flex justify-between items-center gap-2
                  hover:bg-primary600 hover:text-a11y cursor-pointer font-circular ${
                    matchRoutes(action.patterns!, pathname) !== null
                      ? `bg-primary600 text-a11y font-circular tracking-wider`
                      : "text-a11y/80"
                  }`}
                  onClick={() => {
                    toggleSidebar && toggleSidebar()
                  }}
                >
                  {action.title}
                  {action.badge && (
                    <Tag
                      color={getSidenavBadgeColor(action.badge)}
                      className="mr-0"
                    >
                      <span className="text-xs font-circular ">
                        {action.badge}
                      </span>
                    </Tag>
                  )}
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    )
  }

  const leafActive =
    typeof action.patterns !== "undefined" &&
    matchRoutes(action.patterns!, pathname) !== null

  const link = (
    <Link
      id={id}
      to={action.route!}
      key={index!}
      aria-label={action.title}
      target={action.newTab === true ? "_blank" : ""}
      className={`group w-full flex justify-start items-center gap-3 ${itemPadding}
       hover:bg-primary600 hover:text-a11y hover:rounded-md ${
         leafActive
           ? `bg-primary600 text-a11y rounded-md  font-circular tracking-wider`
           : "text-a11y/80 tracking-wide"
       }`}
      onClick={() => {
        toggleSidebar && toggleSidebar()
      }}
    >
      <span className={iconTint(leafActive)}>{action.icon}</span>
      <span
        aria-hidden={compact}
        className={`flex items-center gap-3 font-circular ${fadeOutWhenCompact(
          compact
        )}`}
      >
        <span className="mt-0.5">{action.title}</span>
        {badge}
      </span>
    </Link>
  )

  return (
    <div>
      {compact ? (
        <Tooltip
          placement="right"
          title={action.title}
          mouseEnterDelay={0.15}
        >
          {link}
        </Tooltip>
      ) : (
        link
      )}
    </div>
  )
}
